import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useStockItem,
  useStockOptions,
  useCreateStockItem,
  useUpdateStockItem,
} from '@saiens/shared/hooks';
import { compressImage } from '@saiens/shared/utils';
import { toast } from '@saiens/shared/stores';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, Camera, Upload, X } from 'lucide-react';

export function StockFormPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const numericId = id ? parseInt(id, 10) : undefined;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Data
  const { data: existing, isLoading: existingLoading } = useStockItem(numericId);
  const { data: options } = useStockOptions();
  const createMutation = useCreateStockItem();
  const updateMutation = useUpdateStockItem();

  // Form state
  const [brandCode, setBrandCode] = useState('');
  const [modelNumber, setModelNumber] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [currentLocation, setCurrentLocation] = useState('');
  const [status, setStatus] = useState('in_stock');
  const [warehouseLocation, setWarehouseLocation] = useState('');
  const [longSide, setLongSide] = useState('');
  const [shortSide, setShortSide] = useState('');
  const [thicknessMm, setThicknessMm] = useState('');
  const [isIrregular, setIsIrregular] = useState(false);
  const [hasDefect, setHasDefect] = useState(false);
  const [needsScrap, setNeedsScrap] = useState(false);
  const [scrapReason, setScrapReason] = useState('');
  const [scrapReasonNote, setScrapReasonNote] = useState('');
  const [reservedBy, setReservedBy] = useState('');
  const [reservedCase, setReservedCase] = useState('');
  const [notes, setNotes] = useState('');
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // Initialize form with existing data
  useEffect(() => {
    if (existing && isEdit) {
      setBrandCode(existing.brand_code || '');
      setModelNumber(existing.model_number || '');
      setCaseNumber(existing.case_number || '');
      setCurrentLocation(existing.current_location || '');
      setStatus(existing.status || 'in_stock');
      setWarehouseLocation(existing.warehouse_location || '');
      setLongSide(existing.long_side ? String(existing.long_side) : '');
      setShortSide(existing.short_side ? String(existing.short_side) : '');
      setThicknessMm(existing.thickness_mm || '');
      setIsIrregular(existing.is_irregular || false);
      setHasDefect(existing.has_defect || false);
      setNeedsScrap(existing.needs_scrap || false);
      setScrapReason(existing.scrap_reason || '');
      setScrapReasonNote(existing.scrap_reason_note || '');
      setReservedBy(existing.reserved_by || '');
      setReservedCase(existing.reserved_case || '');
      setNotes(existing.notes || '');
    }
  }, [existing, isEdit]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('檔案格式錯誤', '請選擇圖片檔案');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('檔案過大', '圖片大小不能超過 10MB');
      return;
    }

    try {
      const { base64, previewUrl } = await compressImage(file);
      setPhotoBase64(base64);
      setPhotoPreview(previewUrl);
    } catch {
      toast.error('處理失敗', '無法處理圖片');
    }

    e.target.value = '';
  }, []);

  const handleSave = async () => {
    if (!brandCode || !modelNumber || !longSide || !shortSide || !thicknessMm) {
      toast.error('請填寫必填欄位', '品牌、型號、尺寸為必填');
      return;
    }

    const payload = {
      brand_code: brandCode,
      model_number: modelNumber,
      case_number: caseNumber || undefined,
      current_location: currentLocation,
      status,
      warehouse_location: warehouseLocation || undefined,
      long_side: parseInt(longSide, 10),
      short_side: parseInt(shortSide, 10),
      thickness_mm: thicknessMm,
      is_irregular: isIrregular,
      has_defect: hasDefect,
      needs_scrap: needsScrap,
      scrap_reason: needsScrap ? scrapReason : undefined,
      scrap_reason_note: scrapReason === 'other' ? scrapReasonNote : undefined,
      reserved_by: status === 'reserved' ? reservedBy : undefined,
      reserved_case: status === 'reserved' ? reservedCase : undefined,
      notes: notes || undefined,
      photo: photoBase64 || undefined,
    };

    if (isEdit && numericId) {
      updateMutation.mutate(
        { id: numericId, data: payload },
        {
          onSuccess: () => navigate(`/stock/${numericId}`),
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (result) => navigate(`/stock/${result.id}`),
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEdit && existingLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pb-32 px-4 py-6">
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="拍攝照片"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="從相簿選擇"
      />

      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-2 flex items-center gap-1 text-sm text-gray-500"
        >
          <ArrowLeft className="h-4 w-4" />
          返回
        </button>
        <h1 className="text-xl font-bold text-gray-900">
          {isEdit ? '編輯餘料' : '新增餘料'}
        </h1>
      </div>

      <div className="space-y-4">
        {/* Section: 基本資訊 */}
        <FormSection title="基本資訊">
          <FormField label="品牌代碼" required>
            <select
              value={brandCode}
              onChange={(e) => setBrandCode(e.target.value)}
              className={selectClass}
            >
              <option value="">-- 請選擇 --</option>
              {options?.brand_codes.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="型號" required>
            <input
              type="text"
              value={modelNumber}
              onChange={(e) => setModelNumber(e.target.value)}
              placeholder="輸入型號"
              className={inputClass}
            />
          </FormField>

          <FormField label="案號">
            <input
              type="text"
              value={caseNumber}
              onChange={(e) => setCaseNumber(e.target.value)}
              placeholder="輸入案號（選填）"
              className={inputClass}
            />
          </FormField>
        </FormSection>

        {/* Section: 位置與狀態 */}
        <FormSection title="位置與狀態">
          <FormField label="目前位置">
            <input
              type="text"
              value={currentLocation}
              onChange={(e) => setCurrentLocation(e.target.value)}
              placeholder="輸入位置"
              className={inputClass}
            />
          </FormField>

          <FormField label="狀態">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={selectClass}
            >
              {options?.statuses.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              )) ?? (
                <>
                  <option value="in_stock">在庫</option>
                  <option value="reserved">已預定</option>
                  <option value="scrapped">已報廢</option>
                </>
              )}
            </select>
          </FormField>

          <FormField label="倉庫位置">
            <select
              value={warehouseLocation}
              onChange={(e) => setWarehouseLocation(e.target.value)}
              className={selectClass}
            >
              <option value="">-- 請選擇 --</option>
              {options?.warehouse_locations.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FormField>
        </FormSection>

        {/* Section: 尺寸 */}
        <FormSection title="尺寸">
          <div className="flex gap-3">
            <FormField label="長邊 (mm)" required className="flex-1">
              <input
                type="number"
                value={longSide}
                onChange={(e) => setLongSide(e.target.value)}
                placeholder="長"
                className={inputClass}
              />
            </FormField>
            <FormField label="短邊 (mm)" required className="flex-1">
              <input
                type="number"
                value={shortSide}
                onChange={(e) => setShortSide(e.target.value)}
                placeholder="短"
                className={inputClass}
              />
            </FormField>
          </div>

          <FormField label="厚度" required>
            <select
              value={thicknessMm}
              onChange={(e) => setThicknessMm(e.target.value)}
              className={selectClass}
            >
              <option value="">-- 請選擇 --</option>
              {options?.thicknesses.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FormField>

          <ToggleField
            label="不規則形狀"
            value={isIrregular}
            onChange={setIsIrregular}
          />
        </FormSection>

        {/* Section: 品質 */}
        <FormSection title="品質">
          <ToggleField
            label="有瑕疵"
            value={hasDefect}
            onChange={setHasDefect}
          />
          <ToggleField
            label="需報廢"
            value={needsScrap}
            onChange={setNeedsScrap}
          />

          {needsScrap && (
            <>
              <FormField label="報廢原因">
                <select
                  value={scrapReason}
                  onChange={(e) => setScrapReason(e.target.value)}
                  className={selectClass}
                >
                  <option value="">-- 請選擇 --</option>
                  {options?.scrap_reasons.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </FormField>

              {scrapReason === 'other' && (
                <FormField label="原因說明">
                  <input
                    type="text"
                    value={scrapReasonNote}
                    onChange={(e) => setScrapReasonNote(e.target.value)}
                    placeholder="說明報廢原因"
                    className={inputClass}
                  />
                </FormField>
              )}
            </>
          )}
        </FormSection>

        {/* Section: 預定（only when status=reserved） */}
        {status === 'reserved' && (
          <FormSection title="預定資訊">
            <FormField label="預定人">
              <input
                type="text"
                value={reservedBy}
                onChange={(e) => setReservedBy(e.target.value)}
                placeholder="輸入預定人姓名"
                className={inputClass}
              />
            </FormField>
            <FormField label="預定案號">
              <input
                type="text"
                value={reservedCase}
                onChange={(e) => setReservedCase(e.target.value)}
                placeholder="輸入預定案號"
                className={inputClass}
              />
            </FormField>
          </FormSection>
        )}

        {/* Section: 照片 */}
        <FormSection title="照片">
          {!photoPreview ? (
            <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-4">
              <div className="flex flex-col items-center">
                <p className="mb-3 text-sm text-gray-500">拍攝或上傳餘料照片（選填）</p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white"
                  >
                    <Camera className="h-4 w-4" />
                    拍照
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
                  >
                    <Upload className="h-4 w-4" />
                    相簿
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              <img
                src={photoPreview}
                alt="餘料照片"
                className="w-full rounded-lg object-contain"
                style={{ maxHeight: '200px' }}
              />
              <button
                type="button"
                onClick={() => {
                  setPhotoBase64(null);
                  setPhotoPreview(null);
                }}
                aria-label="清除照片"
                className="absolute right-2 top-2 rounded-full bg-black/50 p-1.5 text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </FormSection>

        {/* Section: 備註 */}
        <FormSection title="備註">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="輸入備註（選填）"
            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </FormSection>
      </div>

      {/* Bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t bg-white p-4">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={isPending}
            className="flex-1 rounded-xl border border-gray-300 py-3 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isPending}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-3 font-medium text-white hover:bg-primary/90 disabled:bg-gray-300"
          >
            {isPending ? (
              <>
                <LoadingSpinner size="sm" />
                儲存中...
              </>
            ) : (
              '儲存'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputClass =
  'w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

const selectClass =
  'w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

function FormSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm">
      <div className="border-b px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
      </div>
      <div className="space-y-3 p-4">{children}</div>
    </div>
  );
}

function FormField({
  label,
  required,
  children,
  className,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs text-gray-500">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function ToggleField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-700">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative h-6 w-11 rounded-full transition-colors ${
          value ? 'bg-primary' : 'bg-gray-200'
        }`}
        aria-label={label}
      >
        <span
          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
            value ? 'translate-x-5' : ''
          }`}
        />
      </button>
    </div>
  );
}
